import React, { useMemo, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Check, Edit2, MonitorPlay, ChevronDown, ChevronRight, Image as ImageIcon } from 'lucide-react';
// --- Types & Initial Data ---
type Option = {
  id: string;
  name: string;
  price: number;
  isStandard: boolean;
  description: string;
  variantSetId: string;
  color: string; // For the placeholder thumbnail
};
type VariantSet = {
  id: string;
  name: string;
  roomId: string;
};
type Room = {
  id: string;
  name: string;
};
const INITIAL_ROOMS: Room[] = [{
  id: 'kitchen',
  name: 'Kitchen'
}, {
  id: 'bathroom',
  name: 'Bathroom'
}, {
  id: 'bedroom',
  name: 'Bedroom'
}, {
  id: 'living',
  name: 'Living Room'
}];
const INITIAL_VARIANT_SETS: VariantSet[] = [{
  id: 'countertops',
  name: 'Countertops',
  roomId: 'kitchen'
}, {
  id: 'appliances',
  name: 'Appliance Package',
  roomId: 'kitchen'
}, {
  id: 'backsplash',
  name: 'Backsplash',
  roomId: 'kitchen'
}];
const INITIAL_OPTIONS: Option[] = [
// Countertops
{
  id: 'opt-c1',
  name: 'Laminate Countertops',
  price: 0,
  isStandard: true,
  description: 'Standard laminate countertops',
  variantSetId: 'countertops',
  color: 'bg-stone-200'
}, {
  id: 'opt-c2',
  name: 'White Quartz Countertops',
  price: 4500,
  isStandard: false,
  description: 'Premium white quartz',
  variantSetId: 'countertops',
  color: 'bg-slate-100'
}, {
  id: 'opt-c3',
  name: 'Marble Countertops',
  price: 8200,
  isStandard: false,
  description: 'Luxury natural marble',
  variantSetId: 'countertops',
  color: 'bg-zinc-300'
},
// Appliances
{
  id: 'opt-a1',
  name: 'Black + White Appliances',
  price: 0,
  isStandard: true,
  description: 'Classic appliance package',
  variantSetId: 'appliances',
  color: 'bg-neutral-800'
}, {
  id: 'opt-a2',
  name: 'Stainless Steel Appliances',
  price: 2500,
  isStandard: false,
  description: 'Modern stainless steel',
  variantSetId: 'appliances',
  color: 'bg-slate-400'
}, {
  id: 'opt-a3',
  name: 'Built-in Appliances',
  price: 8500,
  isStandard: false,
  description: 'Seamless built-in look',
  variantSetId: 'appliances',
  color: 'bg-zinc-600'
},
// Backsplash
{
  id: 'opt-b1',
  name: 'Ceramic Tile',
  price: 0,
  isStandard: true,
  description: 'Standard ceramic tile',
  variantSetId: 'backsplash',
  color: 'bg-orange-100'
}, {
  id: 'opt-b2',
  name: 'Subway Tile',
  price: 1200,
  isStandard: false,
  description: 'Classic white subway tile',
  variantSetId: 'backsplash',
  color: 'bg-white'
}, {
  id: 'opt-b3',
  name: 'Natural Stone',
  price: 3800,
  isStandard: false,
  description: 'Premium natural stone',
  variantSetId: 'backsplash',
  color: 'bg-stone-400'
}];
export function VisualizerPreviewPage() {
  // State
  const [activeRoom, setActiveRoom] = useState('kitchen');
  const [activeVariantSet, setActiveVariantSet] = useState('countertops');
  const [options, setOptions] = useState<Option[]>(INITIAL_OPTIONS);
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  // Buyer Selections (what the buyer has currently selected in the visualizer)
  const [selections, setSelections] = useState<Record<string, string>>({
    countertops: 'opt-c1',
    appliances: 'opt-a1',
    backsplash: 'opt-b1'
  });
  // Derived Data
  const currentVariantSets = INITIAL_VARIANT_SETS.filter(vs => vs.roomId === activeRoom);
  const currentOptions = options.filter(o => o.variantSetId === activeVariantSet);
  const totalUpgradePrice = useMemo(() => {
    return Object.values(selections).reduce((total, optId) => {
      const opt = options.find(o => o.id === optId);
      return total + (opt?.price || 0);
    }, 0);
  }, [selections, options]);
  // Handlers
  const handleOptionChange = (id: string, field: keyof Option, value: any) => {
    setOptions(prev => prev.map(opt => opt.id === id ? {
      ...opt,
      [field]: value
    } : opt));
  };
  const handleSelectBuyerOption = (variantSetId: string, optionId: string) => {
    setSelections(prev => ({
      ...prev,
      [variantSetId]: optionId
    }));
  };
  return <div className="flex flex-col h-[calc(100vh-4rem)] w-full overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-7866">
      {/* TOP PANE: Buyer Visualizer Preview (~45% height) */}
      <div className="h-[45%] min-h-[320px] relative bg-black overflow-hidden flex-shrink-0" data-id="element-7867">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 scale-105" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1600&q=80")'
      }} data-id="element-7868" />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/40" data-id="element-7869" />

        {/* PREVIEW Badge */}
        <div className="absolute top-4 right-4 z-50" data-id="element-7870">
          <div className="bg-black/50 backdrop-blur-md border border-white/10 text-white/70 text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full" data-id="element-7871">
            Live Preview
          </div>
        </div>

        {/* Visualizer UI Container */}
        <div className="absolute inset-0 z-20 flex flex-col justify-between pointer-events-none" data-id="element-7872">
          {/* Top Bar */}
          <div className="p-4 flex items-start justify-between" data-id="element-7873">
            <div className="flex items-center gap-6" data-id="element-7874">
              <div className="text-xl font-black text-white tracking-tight" data-id="element-7875">
                RESVR
              </div>

              <div className="hidden md:flex bg-black/40 backdrop-blur-md border border-white/10 rounded-full p-1 pointer-events-auto" data-id="element-7876">
                {INITIAL_ROOMS.map(room => <button key={room.id} onClick={() => setActiveRoom(room.id)} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${activeRoom === room.id ? 'bg-white/20 text-white shadow-sm' : 'text-white/60 hover:text-white hover:bg-white/10'}`} data-id="element-7877">
                    {room.name}
                  </button>)}
              </div>
            </div>

            <div className="text-right mt-0.5" data-id="element-7878">
              <div className="text-white text-sm font-medium" data-id="element-7879">The Lawson</div>
              <div className="text-white/60 text-xs" data-id="element-7880">2,400 sq ft</div>
            </div>
          </div>

          {/* Main Content Area (Options Carousel) */}
          <div className="w-full pb-4 pointer-events-auto" data-id="element-7881">
            <div className="px-6 mb-2" data-id="element-7882">
              <h2 className="text-white text-lg font-light tracking-wide" data-id="element-7883">
                {INITIAL_VARIANT_SETS.find(vs => vs.id === activeVariantSet)?.name || 'Select Options'}
              </h2>
            </div>

            <div className="flex gap-3 px-6 overflow-x-auto hide-scrollbar pb-2" data-id="element-7884">
              {currentOptions.map(option => {
              const isSelected = selections[activeVariantSet] === option.id;
              return <button key={option.id} onClick={() => handleSelectBuyerOption(activeVariantSet, option.id)} className={`relative flex-shrink-0 w-56 rounded-xl overflow-hidden text-left transition-all duration-300 group ${isSelected ? 'ring-2 ring-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.3)] scale-100' : 'ring-1 ring-white/20 hover:ring-white/40 scale-95 opacity-80 hover:opacity-100'}`} data-id="element-7885">
                    {/* Glass backdrop */}
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-xl" data-id="element-7886" />

                    {/* Content */}
                    <div className="relative p-3 flex flex-col h-full" data-id="element-7887">
                      {/* Thumbnail Placeholder */}
                      <div className={`w-full h-20 rounded-lg mb-3 ${option.color} shadow-inner flex items-center justify-center overflow-hidden relative`} data-id="element-7888">
                        <ImageIcon className="h-6 w-6 text-black/10" data-id="element-7889" />
                        {/* Subtle gradient overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" data-id="element-7890" />
                      </div>

                      <div className="flex-1" data-id="element-7891">
                        <h4 className="text-white font-medium text-sm leading-tight mb-0.5 truncate" data-id="element-7892">
                          {option.name}
                        </h4>
                        <p className={`text-xs font-medium ${isSelected ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-7893">
                          {option.isStandard ? 'Included' : `+$${option.price.toLocaleString()}`}
                        </p>
                      </div>

                      {/* Selection Indicator */}
                      <div className={`absolute top-4 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-cyan-400 border-cyan-400' : 'border-white/30 bg-black/20'}`} data-id="element-7894">
                        {isSelected && <Check className="h-2.5 w-2.5 text-black stroke-[3]" data-id="element-7895" />}
                      </div>
                    </div>
                  </button>;
            })}
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="bg-black/80 backdrop-blur-xl border-t border-white/10 p-4 flex items-center justify-between pointer-events-auto" data-id="element-7896">
            <div className="flex items-center gap-6" data-id="element-7897">
              <div data-id="element-7898">
                <div className="text-white/50 text-[10px] uppercase tracking-wider font-bold mb-0.5" data-id="element-7899">
                  Your Selections
                </div>
                <div className="text-white text-sm font-medium" data-id="element-7900">
                  {Object.keys(selections).length} of{' '}
                  {INITIAL_VARIANT_SETS.length} options selected
                </div>
              </div>
              <div className="h-8 w-px bg-white/10" data-id="element-7901" />
              <div data-id="element-7902">
                <div className="text-white/50 text-[10px] uppercase tracking-wider font-bold mb-0.5" data-id="element-7903">
                  Total Upgrades
                </div>
                <div className="text-cyan-400 font-medium text-lg" data-id="element-7904">
                  +${totalUpgradePrice.toLocaleString()}
                </div>
              </div>
            </div>

            <button className="bg-white text-black px-6 py-2 text-sm rounded-full font-semibold hover:bg-slate-200 transition-colors" data-id="element-7905">
              Continue
            </button>
          </div>
        </div>
      </div>

      {/* BOTTOM PANE: Admin Editor (~55% height) */}
      <div className="flex-1 bg-slate-50 border-t border-slate-200 flex flex-col overflow-hidden z-10" data-id="element-7906">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-between flex-shrink-0" data-id="element-7907">
          <div data-id="element-7908">
            <div className="flex items-center gap-2 mb-0.5" data-id="element-7909">
              <MonitorPlay className="h-5 w-5 text-primary-600" data-id="element-7910" />
              <h1 className="text-lg font-bold text-slate-900" data-id="element-7911">
                Visualizer Editor
              </h1>
            </div>
            <p className="text-sm text-slate-500" data-id="element-7912">
              Edit options below and see changes instantly in the preview above
            </p>
          </div>
        </div>

        {/* Room Tabs */}
        <div className="px-6 pt-3 border-b border-slate-200 bg-white flex-shrink-0" data-id="element-7913">
          <div className="flex space-x-6 overflow-x-auto hide-scrollbar" data-id="element-7914">
            {INITIAL_ROOMS.map(room => <button key={room.id} onClick={() => {
            setActiveRoom(room.id);
            const firstVs = INITIAL_VARIANT_SETS.find(vs => vs.roomId === room.id);
            if (firstVs) setActiveVariantSet(firstVs.id);
          }} className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeRoom === room.id ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-7915">
                {room.name}
              </button>)}
          </div>
        </div>

        {/* Variant Sets & Options */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-7916">
          <div className="max-w-7xl mx-auto space-y-8" data-id="element-7917">
            {currentVariantSets.map(vs => <div key={vs.id} className="space-y-4" data-id="element-7918">
                <button onClick={() => setActiveVariantSet(vs.id)} className="flex items-center gap-2 group w-full text-left" data-id="element-7919">
                  {activeVariantSet === vs.id ? <ChevronDown className="h-5 w-5 text-primary-600" data-id="element-7920" /> : <ChevronRight className="h-5 w-5 text-slate-400" data-id="element-7921" />}
                  <h3 className={`text-base font-bold uppercase tracking-wider ${activeVariantSet === vs.id ? 'text-primary-700' : 'text-slate-500 group-hover:text-slate-700'}`} data-id="element-7922">
                    {vs.name}
                  </h3>
                </button>

                {activeVariantSet === vs.id && <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 pl-7 items-start" data-id="element-7923">
                    {options.filter(o => o.variantSetId === vs.id).map(option => {
                const isSelectedByBuyer = selections[vs.id] === option.id;
                const isEditing = editingOptionId === option.id;
                return <Card key={option.id} className={`overflow-hidden transition-all ${isEditing ? 'ring-2 ring-primary-500 border-primary-500 shadow-md' : 'hover:border-slate-300'}`} data-id="element-7924">
                            {/* Card Header / Display */}
                            <div className={`p-4 cursor-pointer flex items-start gap-3 ${isEditing ? 'bg-slate-50 border-b border-slate-100' : ''}`} onClick={() => setEditingOptionId(isEditing ? null : option.id)} data-id="element-7925">
                              <button onClick={e => {
                      e.stopPropagation();
                      handleSelectBuyerOption(vs.id, option.id);
                    }} className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelectedByBuyer ? 'bg-primary-600 border-primary-600' : 'border-slate-300 hover:border-primary-400'}`} data-id="element-7926">
                                {isSelectedByBuyer && <Check className="h-3 w-3 text-white" data-id="element-7927" />}
                              </button>

                              <div className="flex-1 min-w-0" data-id="element-7928">
                                <div className="flex items-center justify-between gap-2 mb-1" data-id="element-7929">
                                  <span className="font-medium text-slate-900 truncate" data-id="element-7930">
                                    {option.name}
                                  </span>
                                  <Badge variant={option.isStandard ? 'success' : 'neutral'} className="flex-shrink-0" data-id="element-7931">
                                    {option.isStandard ? 'Included' : `$${option.price.toLocaleString()}`}
                                  </Badge>
                                </div>
                                <p className="text-xs text-slate-500 line-clamp-1" data-id="element-7932">
                                  {option.description}
                                </p>
                              </div>

                              <Edit2 className={`h-4 w-4 flex-shrink-0 mt-1 ${isEditing ? 'text-primary-600' : 'text-slate-300'}`} data-id="element-7933" />
                            </div>

                            {/* Edit Form */}
                            {isEditing && <div className="p-4 space-y-4 bg-white" data-id="element-7934">
                                <div data-id="element-7935">
                                  <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7936">
                                    Option Name
                                  </label>
                                  <input type="text" value={option.name} onChange={e => handleOptionChange(option.id, 'name', e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-7937" />
                                </div>
                                <div className="flex gap-4" data-id="element-7938">
                                  <div className="flex-1" data-id="element-7939">
                                    <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7940">
                                      Price ($)
                                    </label>
                                    <input type="number" value={option.price} disabled={option.isStandard} onChange={e => handleOptionChange(option.id, 'price', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none disabled:bg-slate-50 disabled:text-slate-400" data-id="element-7941" />
                                  </div>
                                  <div className="flex items-end pb-2" data-id="element-7942">
                                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-7943">
                                      <input type="checkbox" checked={option.isStandard} onChange={e => {
                            handleOptionChange(option.id, 'isStandard', e.target.checked);
                            if (e.target.checked) handleOptionChange(option.id, 'price', 0);
                          }} className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" data-id="element-7944" />
                                      <span className="text-sm text-slate-700" data-id="element-7945">
                                        Standard
                                      </span>
                                    </label>
                                  </div>
                                </div>
                                <div data-id="element-7946">
                                  <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-7947">
                                    Description
                                  </label>
                                  <textarea value={option.description} onChange={e => handleOptionChange(option.id, 'description', e.target.value)} rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none" data-id="element-7948" />
                                </div>
                              </div>}
                          </Card>;
              })}
                  </div>}
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}